import type { SaleOrderLine, SaleOrderSourceLine } from "../types";
import { isBhmrPackageLine, resolveBhmrPackage } from "./bhmrAutoLink";

/**
 * Gộp các dòng đơn cùng mã hàng thành một dòng để hiển thị và đếm số lượng quét.
 *
 * Khác bản cũ (mergeInventoryTransactions trong WarehouseOutWardActionV2):
 * - Chỉ cộng các trường số lượng. Đơn giá (poPrice, soPrice1, soPrice2) giữ của dòng đầu,
 *   không cộng dồn, vì cộng đơn giá làm Thành tiền sai khi một mã hàng nằm ở nhiều dòng.
 * - Giữ lại toàn bộ sourceLineId trong `sourceLineIds` (cần cho BHMR, liên kết theo dòng đơn).
 * - Dòng gói BHMR: nối ATTRIBUTE38 / ATTRIBUTE39 các dòng cùng mã rồi tách vào `bhmr` (resolveBhmrPackage).
 *   Màn đơn dồn suất máy trong đơn vào dòng gói đầu tiên, còn serial máy ngoài đơn nằm ở từng dòng.
 * - Không sửa mảng đầu vào.
 */
export const mergeOrderLines = (
  lines: SaleOrderSourceLine[] | null | undefined,
): SaleOrderLine[] => {
  const grouped = new Map<string, SaleOrderLine>();

  (lines ?? []).forEach((line) => {
    if (!line.itemCode) return;

    const existing = grouped.get(line.itemCode);
    if (!existing) {
      grouped.set(line.itemCode, {
        ...line,
        sourceLineIds: line.sourceLineId != null ? [line.sourceLineId] : [],
      });
      return;
    }

    existing.primaryQuantity =
      (existing.primaryQuantity ?? 0) + (line.primaryQuantity ?? 0);
    existing.subQuantity = (existing.subQuantity ?? 0) + (line.subQuantity ?? 0);
    existing.transactionQuantity =
      (existing.transactionQuantity ?? 0) + (line.transactionQuantity ?? 0);
    existing.limitQuantity =
      (existing.limitQuantity ?? 0) + (line.limitQuantity ?? 0);
    existing.scannedQuantity =
      (existing.scannedQuantity ?? 0) + (line.scannedQuantity ?? 0);
    existing.remainQuantity =
      (existing.remainQuantity ?? 0) + (line.remainQuantity ?? 0);
    if (line.sourceLineId != null) {
      existing.sourceLineIds = [...(existing.sourceLineIds ?? []), line.sourceLineId];
    }
    if (isBhmrPackageLine(line)) {
      const join = (a?: string | null, b?: string | null) =>
        [a, b].map((s) => (s ?? "").trim()).filter(Boolean).join(", ");
      existing.attribute38 = join(existing.attribute38, line.attribute38);
      existing.attribute39 = join(existing.attribute39, line.attribute39);
    }
    // Xuất đổi trả: "Serial đổi" của các dòng cùng mã hàng gộp lại để gợi ý cho cột "Serial nhập"
    if (line.attribute22) {
      existing.attribute22 = [existing.attribute22, line.attribute22]
        .map((s) => (s ?? "").trim())
        .filter(Boolean)
        .join(",");
    }
  });

  return Array.from(grouped.values(), (line) =>
    isBhmrPackageLine(line) ? { ...line, bhmr: resolveBhmrPackage(line, lines ?? []) } : line,
  );
};
